<script setup lang="ts">
import { computed } from 'vue'
import { ORDER_STATUS } from '@/constants/orderStatus'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { ArrowTopRightOnSquareIcon, CalendarDaysIcon, CheckCircleIcon, NoSymbolIcon, XCircleIcon } from '@heroicons/vue/24/outline'
import SendAdvRequirementsInfo from './SendAdvRequirementsInfo.vue'

const props = defineProps<{
  status: string
  isAssignedToMe: boolean
  isAuthorOrCommercial: boolean
  canSendToAdv: boolean
  hideSendAdv?: boolean
  isChildFollowingParent?: boolean
  sendAdvBlockReason: string | null
  sendAdvRequirements: { signed: boolean; typeContrat: boolean; modePaiement: boolean; famille: boolean; devisSigne?: boolean }
  isSaving: boolean
  isUpdatingStatus: boolean
  isClaiming: boolean
  isAdvManager?: boolean
  processingDate?: string | null
  isEditMode?: boolean
  canValidateBuManager?: boolean
}>()

const emit = defineEmits<{
  (e: 'save', backToDraft?: boolean): void
  (e: 'send-adv'): void
  (e: 'resend-adv'): void
  (e: 'back-to-draft'): void
  (e: 'claim'): void
  (e: 'block-business'): void
  (e: 'block-operator'): void
  (e: 'cancel'): void
  (e: 'delete'): void
  (e: 'validate'): void
  (e: 'unvalidate'): void
  (e: 'unblock'): void
  (e: 'view-parent'): void
  (e: 'info-hover', value: boolean): void
  (e: 'open-processing-date-modal'): void
  (e: 'validate-bu-manager'): void
  (e: 'refuse-bu-manager'): void
}>()
const isDraft = computed(() => {
  const s = String(props.status || '').toUpperCase()
  return s === ORDER_STATUS.DRAFT || s === 'BROUILLON'
})
const isWaiting = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.WAITING)
const isInProgress = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.IN_PROGRESS)
const isBlockedBusiness = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.BLOCKED_BUSINESS)
const isBlockedOperator = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.BLOCKED_OPERATOR)
const isCanceled = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.CANCELED)
const isValidated = computed(() => String(props.status || '').toUpperCase() === ORDER_STATUS.VALIDATED)

// Sur un enfant en "Workflow parent" : on cache toutes les actions sauf Enregistrer,
// et on expose un bouton "Voir Commande Principale" à la place.
const isChildLocked = computed(() => props.isChildFollowingParent === true)

const canShowClaim = computed(() => isWaiting.value && !props.isAssignedToMe && !isChildLocked.value)

const canShowSave = computed(() => {
  return isWaiting.value || isInProgress.value || isBlockedBusiness.value || isBlockedOperator.value || isDraft.value
})

const canShowCancel = computed(() => props.isEditMode && (isWaiting.value || isInProgress.value || isDraft.value) && !isChildLocked.value)

const canShowDelete = computed(() => props.isEditMode && (isDraft.value || isCanceled.value) && !isChildLocked.value)
const canShowBlockBusiness = computed(() => isInProgress.value && !isChildLocked.value)
const canShowBlockOperator = computed(() => isInProgress.value && !isChildLocked.value)
const canShowValidate = computed(() => isInProgress.value && !isChildLocked.value)

const canShowUnblock = computed(() => (isBlockedBusiness.value || isBlockedOperator.value) && !isChildLocked.value)

const canShowUnvalidate = computed(() => isValidated.value && !isChildLocked.value)
const canShowEditDate = computed(() => (isValidated.value || isInProgress.value) && !isChildLocked.value)

const canShowSendAdv = computed(() => isDraft.value && !props.hideSendAdv && !isChildLocked.value)

const canShowBackToDraft = computed(() => isWaiting.value && props.isAuthorOrCommercial && !isChildLocked.value)

const canShowResendAdv = computed(() => isBlockedBusiness.value && props.isAuthorOrCommercial && !props.hideSendAdv && !isChildLocked.value)

const req = computed(() => props.sendAdvRequirements ?? { signed: false, typeContrat: false, modePaiement: false, famille: false, devisSigne: true })
</script>

<template>
  <div class="flex gap-3 shrink-0">
    <ButtonDaisy
      v-if="isChildLocked"
      text="Voir Commande Principale"
      variant="primary"
      size="sm"
      :icon="ArrowTopRightOnSquareIcon"
      @click="emit('view-parent')"
    />
    <ButtonDaisy v-if="canShowBackToDraft" text="Passer en brouillon" variant="secondary" size="sm" :loading="isSaving" @click="emit('save', true)" />
    <ButtonDaisy
      v-if="canShowDelete"
      :text="isCanceled ? 'Supprimer la commande' : 'Supprimer le brouillon'"
      variant="error"
      size="sm"
      :loading="isUpdatingStatus"
      @click="emit('delete')"
    />

    <div v-if="canShowResendAdv" class="flex items-center gap-1">
      <SendAdvRequirementsInfo :requirements="req" @info-hover="emit('info-hover', $event)" />
      <div :title="sendAdvBlockReason ?? undefined">
        <ButtonDaisy
          text="Renvoyer ADV"
          variant="primary"
          size="sm"
          :disabled="!canSendToAdv"
          :loading="isUpdatingStatus"
          @click="emit('resend-adv')"
        />
      </div>
    </div>

    <div v-if="canShowSendAdv" class="flex items-center gap-1">
      <SendAdvRequirementsInfo :requirements="req" @info-hover="emit('info-hover', $event)" />
      <div :title="sendAdvBlockReason ?? undefined">
        <ButtonDaisy text="Envoyer ADV" variant="primary" size="sm" :disabled="!canSendToAdv" :loading="isSaving" @click="emit('send-adv')" />
      </div>
    </div>

    <ButtonDaisy v-if="canShowClaim" text="Me l'attribuer" variant="secondary" size="sm" :loader="isClaiming" @click="emit('claim')" />

    <ButtonDaisy v-if="canShowSave" text="Enregistrer" variant="secondary" size="sm" :loading="isSaving" @click="emit('save')" />

    <ButtonDaisy v-if="canShowCancel" text="Annuler" variant="error" size="sm" :loading="isUpdatingStatus" @click="emit('cancel')" />

    <ButtonDaisy
      v-if="canShowBlockBusiness"
      text="Bloquer Business"
      variant="warning"
      size="sm"
      class="bg-yellow-500 border-yellow-500 hover:bg-yellow-600 hover:border-yellow-600 text-white"
      :icon="NoSymbolIcon"
      :loading="isUpdatingStatus"
      @click="emit('block-business')"
    />

    <ButtonDaisy
      v-if="canShowBlockOperator"
      text="Bloquer Opérateur"
      variant="warning"
      size="sm"
      class="bg-yellow-500 border-yellow-500 hover:bg-yellow-600 hover:border-yellow-600 text-white"
      :icon="XCircleIcon"
      :loading="isUpdatingStatus"
      @click="emit('block-operator')"
    />

    <ButtonDaisy v-if="canShowUnblock" text="Débloquer" variant="primary" size="sm" :loading="isUpdatingStatus" @click="emit('unblock')" />

    <ButtonDaisy
      v-if="canValidateBuManager"
      text="Refuser et renvoyer à l'IC"
      variant="error"
      size="sm"
      :icon="NoSymbolIcon"
      :loading="isUpdatingStatus"
      @click="emit('refuse-bu-manager')"
    />

    <ButtonDaisy
      v-if="canValidateBuManager"
      text="Valider Manager et Envoyer ADV"
      variant="primary"
      size="sm"
      style="background-color:#DA70D6;border-color:#DA70D6"
      :loading="isUpdatingStatus"
      @click="emit('validate-bu-manager')"
    />

    <ButtonDaisy
      v-if="canShowValidate"
      text="Valider"
      variant="success"
      size="sm"
      class="bg-emerald-500 border-emerald-500 hover:bg-emerald-600 hover:border-emerald-600 text-white"
      :icon="CheckCircleIcon"
      :loading="isUpdatingStatus"
      @click="emit('validate')"
    />

    <ButtonDaisy
      v-if="canShowUnvalidate"
      text="Dévalider"
      variant="warning"
      size="sm"
      class="bg-amber-500 border-amber-500 hover:bg-amber-600 hover:border-amber-600 text-white"
      :loading="isUpdatingStatus"
      @click="emit('unvalidate')"
    />

    <ButtonDaisy
      v-if="isAdvManager && canShowEditDate"
      text="Modifier date de traitement"
      variant="secondary"
      size="sm"
      :icon="CalendarDaysIcon"
      :loading="isUpdatingStatus"
      @click="emit('open-processing-date-modal')"
    />
  </div>
</template>
